// Composable for managing a single DomainTerm: fetches data, description, and auto-saves changes.
import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import {
    refreshOnCrossTabEntitySync,
    refreshOnExternalEntityChange,
    wouldClobberLocalChanges,
} from "@/composables/refresh-on-entity-change";
import { useStekManager } from "@/composables/stek-manager";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";

import { FILE_SYNC_THROTTLE_MS } from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";
import { registerFlush } from "./pending-flush";
import { clearStaleContentOnSwitch } from "./stale-while-revalidate";
import { isAbortError, useLatestRequest } from "./use-latest-request";

export type DomainTerm = types.DomainTerm;

export const useDomainTermStore = defineEntityStore("domainTerm", () => {
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();
    const domainTerm = ref<DomainTerm | undefined>(undefined);
    const router = useRouter();
    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const { domainTerm: domainTermInfo } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const isFetching = ref(false);
    const isSaving = ref(false);
    // Latest-request guard: each updateDomainTerm gets its own AbortController +
    // token, so a stale run can neither abort the current one nor commit results.
    const { begin, isCurrent } = useLatestRequest();
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(domainTerm);

    async function updateDomainTerm(force = false) {
        const termId = domainTermInfo.value?.identity;
        if (!force && domainTerm.value && domainTerm.value.identity === termId)
            return;

        // Start a new request: aborts the previous one, our own signal + token.
        const { token, signal } = begin();

        if (!termId) {
            description.value = undefined;
            domainTerm.value = undefined;
            return;
        }

        isFetching.value = true;
        // Stale-while-revalidate: keep the previously-loaded term + description on
        // screen while the *same* term is refetched, so it doesn't blank the page
        // (the deferred spinner then never flashes). The data overwrites below;
        // updateDescription always refetches for the current term. Switching to a
        // *different* term is handled by clearStaleContentOnSwitch.

        try {
            const { data, response } =
                await api.startDomainDomainTermMgtGetDomainTermStartPost({
                    body: { domain_term_id: termId },
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!isCurrent(token)) {
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value?.name,
                            stekName: stek.value?.name,
                        },
                        query: {
                            type: "domain_term",
                            id: termId,
                        },
                    });
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Landing-time clobber guard: edits may have appeared while this
            // GET was in flight — the notification-time guard can't see
            // them. Only a same-term refetch is dropped; navigating to a
            // different term must always commit. A dropped commit
            // self-heals: the edit's own sync re-notifies and refetches.
            if (
                termId === domainTerm.value?.identity &&
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }
            domainTerm.value = data as DomainTerm;
            // The fetched row is the new name baseline. Without this, a
            // server-side rename surfacing on a same-term refetch would read
            // as a local edit (the name manager only re-baselines on
            // identity change) and get echoed back as a save.
            persistName();
            // Fetch description on the same signal/token so an abort or a newer
            // request supersedes it too, and isFetching stays true until it's done.
            await updateDescription(signal, token);
        } catch (e) {
            if (isAbortError(e)) return;
            throw e;
        } finally {
            // Only the current request owns the fetch flag.
            if (isCurrent(token)) {
                isFetching.value = false;
            }
        }
    }

    // Drop the previous term's content when the route switches to a different
    // term, so stale-while-revalidate never renders the old one on the new page.
    clearStaleContentOnSwitch(
        () => domainTermInfo.value?.identity,
        [domainTerm, description]
    );
    watch(
        () => domainTermInfo.value?.identity,
        () => updateDomainTerm(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (domainTerm.value) updateDomainTerm(true);
        }
    );

    const updateDescription = async (signal: AbortSignal, token: number) => {
        // Always refetch for the (possibly new) current term — under SWR the
        // previous description stays set, so a `!description.value` guard would
        // wrongly keep the stale value.
        if (domainTerm.value) {
            try {
                const result = await getDescriptionArtefact(
                    domainTerm.value,
                    "domainTermDescription",
                    signal
                );
                // A newer request superseded us — commit nothing. Same for
                // edits that appeared while fetching (landing-time guard).
                if (
                    !isCurrent(token) ||
                    wouldClobberLocalChanges(externalRefresh)
                ) {
                    return;
                }
                description.value = result;
            } catch (e) {
                if (isAbortError(e)) return;
                throw e;
            }
        }
    };

    // No isFetching short-circuit here: edits made WHILE a refetch is in
    // flight must read as modified, or the landing-time clobber guard can't
    // see them and the refetch's response overwrites the user's text.
    const isModified = computed(() =>
        Boolean(isNameModified.value || description.value?.isModified)
    );

    // One shared opts object: the notification guards and the landing-time
    // commit gates inside the update functions must agree on what "would
    // clobber local changes".
    const externalRefresh = {
        entityType: EntityType.DOMAIN_TERM,
        currentEntityId: () => domainTerm.value?.identity,
        isDirty: () => isModified.value,
        refetch: () => updateDomainTerm(true),
    };

    refreshOnExternalEntityChange(externalRefresh);

    const save = async () => {
        if (!isModified.value || !domainTerm.value || isSaving.value) return;

        const body: Record<string, unknown> = {
            domain_term_id: domainTerm.value.identity,
        };

        if (isNameModified.value) body.name = domainTerm.value.name;
        if (description.value?.isModified)
            body.description = description.value.blob;

        // Only update if there's something beyond the id to change.
        if (Object.keys(body).length === 1) return;

        isSaving.value = true;
        try {
            const { response } =
                await api.startDomainDomainTermMgtUpdateDomainTermStartPost({
                    body,
                });
            if (response.ok) {
                if (description.value?.isModified) description.value.persist();
                if (isNameModified.value) persistName();

                // Broadcast the sync so other components can react.
                syncChannel.postMessage({
                    message: "synced",
                    identity: {
                        organizationId: organization.value?.identity,
                        entityType: EntityType.DOMAIN_TERM,
                        entityId: domainTerm.value.identity,
                        attribute: "description",
                    },
                });
            }
        } finally {
            isSaving.value = false;
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(save, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) throttledSave();
    });

    refreshOnCrossTabEntitySync(externalRefresh);

    return { domainTerm, description, isFetching, isModified, reset };
});

export const useDomainTermManager = () => storeToRefs(useDomainTermStore());
